<template>
  <standard-dialog
    v-model="model"
    :title="$t('supplier_po.response_title')"
    :subtitle="subtitle"
    icon="mdi-message-reply-text-outline"
    max-width="960"
    hide-actions
  >
    <div v-if="loading && !view" class="text-center pa-8"><v-progress-circular indeterminate color="primary" /></div>
    <v-alert v-else-if="loadError" type="error" text>{{ loadError }}</v-alert>

    <template v-else-if="view">
      <!-- Slanja -->
      <div class="text-subtitle-2 mb-2">{{ $t("supplier_po.sent_links") }}</div>
      <v-simple-table dense class="mb-4 outlined-table">
        <tbody>
          <tr v-for="d in view.dispatches" :key="d.id">
            <td style="width: 28px"><v-icon small>{{ channelIcon(d.channel) }}</v-icon></td>
            <td>
              {{ dt(d.sent_at) }}
              <span v-if="d.recipients" class="grey--text text--darken-1"> · {{ d.recipients.join(", ") }}</span>
            </td>
            <td>
              <v-chip x-small :color="d.first_opened_at ? 'success' : undefined" :outlined="!d.first_opened_at">
                {{ d.first_opened_at ? $t("supplier_po.opened_at") + " " + dt(d.first_opened_at) : $t("supplier_po.never_opened") }}
              </v-chip>
            </td>
            <td class="text-caption">
              <span v-if="d.revoked_at" class="error--text">{{ $t("supplier_po.revoked") }}</span>
              <span v-else-if="!d.active" class="error--text">{{ $t("supplier_po.expired") }}</span>
              <span v-else>{{ $t("supplier_po.expires_at") }} {{ dt(d.expires_at, true) }}</span>
            </td>
            <td class="text-right">
              <v-btn v-if="d.active" x-small text color="error" :loading="busy === 'rev' + d.id" @click="revoke(d)">
                {{ $t("supplier_po.revoke") }}
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <v-alert v-if="!latest" type="info" text dense>{{ $t("supplier_po.no_response_yet") }}</v-alert>

      <!-- Posljednji odgovor -->
      <template v-if="latest">
        <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
          <div class="text-subtitle-1 font-weight-bold">{{ $t("supplier_po.version", { n: latest.version }) }}</div>
          <v-chip small :color="statusColor(latest.status)" dark>{{ $t("supplier_po.rs_" + latest.status.replace(/-/g, '_')) }}</v-chip>
          <v-chip v-if="latest.decision" small outlined>{{ $t("supplier_po.decision_" + latest.decision) }}</v-chip>
          <span class="text-caption grey--text text--darken-1">{{ dt(latest.created_at) }}</span>
        </div>

        <div class="text-body-2 mb-3">
          <span v-if="latest.delivery_date" class="mr-4"><strong>{{ $t("supplier_po.delivery_date") }}:</strong> {{ d8(latest.delivery_date) }}</span>
          <span class="mr-4">{{ latest.prices_include_vat ? $t("supplier_po.vat_incl") : $t("supplier_po.vat_excl") }}</span>
          <span v-if="latest.contact_name"><strong>{{ $t("supplier_po.contact") }}:</strong> {{ latest.contact_name }}</span>
          <div v-if="latest.note" class="mt-1" style="white-space: pre-wrap"><strong>{{ $t("supplier_po.supplier_note") }}:</strong> {{ latest.note }}</div>
        </div>

        <v-alert v-if="latest.status !== 'reject-supplier'" :type="latest.changes_count ? 'warning' : 'success'" text dense>
          {{ latest.changes_count ? $t("supplier_po.changed_items", { n: latest.changes_count }) : $t("supplier_po.no_changes") }}
        </v-alert>

        <v-simple-table class="outlined-table">
          <thead>
            <tr>
              <th>{{ $t("supplier_po.col_item") }}</th>
              <th class="text-right">{{ $t("supplier_po.col_requested") }}</th>
              <th class="text-right">{{ $t("supplier_po.col_offered") }}</th>
              <th class="text-right">{{ $t("supplier_po.col_price") }}</th>
              <th>{{ $t("supplier_po.col_availability") }}</th>
              <th>{{ $t("supplier_po.col_other") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="i in latest.items" :key="i.id" :class="{ 'changed-row': i.changed }">
              <td>{{ i.name }}</td>
              <td class="text-right">{{ num(i.requested_amount) }} {{ i.unit }}</td>
              <td class="text-right" :class="{ 'font-weight-bold': diffAmount(i) }">
                {{ num(i.offered_amount) }} {{ i.unit }}
              </td>
              <td class="text-right" :class="{ 'font-weight-bold': diffPrice(i) }">
                <span v-if="i.unit_price !== null">{{ money(i.unit_price) }}</span>
                <span v-else class="grey--text">–</span>
                <div v-if="diffPrice(i)" class="text-caption grey--text text--darken-1">
                  ({{ $t("supplier_po.was") }} {{ money(i.requested_unit_price) }})
                </div>
              </td>
              <td>
                <v-chip x-small :color="availColor(i.availability)" dark>{{ $t("supplier_po.av_" + i.availability) }}</v-chip>
              </td>
              <td class="text-caption">
                <div v-if="i.supplier_code">{{ $t("supplier_po.code") }}: {{ i.supplier_code }}</div>
                <div v-if="i.substitute">{{ $t("supplier_po.substitute") }}: {{ i.substitute }}</div>
                <div v-if="i.note">{{ i.note }}</div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td class="font-weight-bold">{{ $t("supplier_po.total") }}</td>
              <td colspan="2" class="text-right text-caption">
                {{ $t("supplier_po.total_requested") }}: {{ money(latest.requested_total) }}
              </td>
              <td class="text-right font-weight-bold">{{ money(latest.offered_total) }}</td>
              <td colspan="2" class="text-caption">{{ $t("supplier_po.total_offered") }}</td>
            </tr>
          </tfoot>
        </v-simple-table>

        <!-- Odluka -->
        <div v-if="canDecide" class="decision mt-4">
          <v-textarea
            v-model="note"
            :label="$t('supplier_po.decision_note')"
            rows="2"
            auto-grow
            outlined
            dense
            hide-details
            maxlength="1000"
          />
          <div class="d-flex flex-wrap mt-3" style="gap: 8px">
            <v-btn color="success" depressed :loading="busy === 'accept'" :disabled="!!busy" @click="decide('accept')">
              <v-icon left>mdi-check</v-icon>{{ $t("supplier_po.accept") }}
            </v-btn>
            <v-btn color="warning" depressed :loading="busy === 'return'" :disabled="!!busy || !note.trim()" @click="decide('return')">
              <v-icon left>mdi-undo-variant</v-icon>{{ $t("supplier_po.return") }}
            </v-btn>
            <v-btn color="error" outlined :loading="busy === 'reject'" :disabled="!!busy" @click="decide('reject')">
              <v-icon left>mdi-close</v-icon>{{ $t("supplier_po.reject") }}
            </v-btn>
          </div>
        </div>
        <div v-else-if="latest.decision" class="text-body-2 mt-3">
          <strong>{{ $t("supplier_po.decision_" + latest.decision) }}</strong>
          <span class="grey--text text--darken-1"> · {{ dt(latest.decided_at) }}</span>
          <div v-if="latest.decision_note" style="white-space: pre-wrap">{{ latest.decision_note }}</div>
        </div>
      </template>

      <!-- Ranije verzije i istorija -->
      <v-expansion-panels v-if="olderResponses.length || view.events.length" flat class="mt-4 outlined-table" multiple>
        <v-expansion-panel v-if="olderResponses.length">
          <v-expansion-panel-header>{{ $t("supplier_po.history") }} ({{ olderResponses.length }})</v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-for="r in olderResponses" :key="r.id" class="mb-2 text-body-2">
              <strong>{{ $t("supplier_po.version", { n: r.version }) }}</strong>
              · {{ $t("supplier_po.rs_" + r.status.replace(/-/g, '_')) }} · {{ dt(r.created_at) }}
              <span v-if="r.decision"> · {{ $t("supplier_po.decision_" + r.decision) }}</span>
              <span v-if="r.changes_count"> · {{ $t("supplier_po.changed_items", { n: r.changes_count }) }}</span>
              <div v-if="r.decision_note" class="grey--text text--darken-1">{{ r.decision_note }}</div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel v-if="view.events.length">
          <v-expansion-panel-header>{{ $t("supplier_po.events") }}</v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-for="e in view.events" :key="e.id" class="text-body-2">
              {{ dt(e.created_at) }} · {{ $t("supplier_po.event_" + e.type) }}
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
  </standard-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";
import StandardDialog from "@/components/dialogs/StandardDialog.vue";

// Odgovor dobavljaca na poslanu narudzbenicu: trazeno / ponudjeno jedno pored drugog,
// razlike istaknute, odluka restorana (prihvati / odbij / vrati dobavljacu).
export default {
  name: "WarehouseSupplierResponseDialog",
  components: { StandardDialog },
  mixins: [hasModel, hasSnackbar],
  props: {
    document: { type: Object, required: true },
  },
  data() {
    return { view: null, loading: false, loadError: "", busy: "", note: "" };
  },
  computed: {
    subtitle() {
      return ["#" + (this.document.number || this.document.id), this.document.supplierName].filter(Boolean).join(" · ");
    },
    latest() {
      return this.view && this.view.responses.length ? this.view.responses[0] : null;
    },
    olderResponses() {
      return this.view ? this.view.responses.slice(1) : [];
    },
    canDecide() {
      return !!(this.view && this.view.awaiting_decision && this.latest && !this.latest.decision);
    },
  },
  watch: {
    model(open) {
      if (open) {
        this.view = null;
        this.note = "";
        this.load();
      }
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        this.view = (await warehouseService.getSupplierResponse(this.document.id)).data;
      } catch (e) {
        this.loadError = this.$t("supplier_po.error_load");
      } finally {
        this.loading = false;
      }
    },
    async decide(decision) {
      this.busy = decision;
      try {
        const { data } = await warehouseService.decideSupplierResponse(this.document.id, this.latest.id, {
          decision,
          note: this.note.trim() || null,
        });
        this.view = data;
        this.note = "";
        this.showSuccessSnackbar(this.$t("supplier_po.decision_saved"));
        this.$emit("changed");
      } catch (e) {
        const res = e.response;
        const first = res && res.data && res.data.errors && Object.values(res.data.errors)[0];
        this.showErrorSnackbar((first && first[0]) || this.$t("supplier_po.decision_error"));
        this.load();
      } finally {
        this.busy = "";
      }
    },
    async revoke(d) {
      this.busy = "rev" + d.id;
      try {
        await warehouseService.revokeSupplierDispatch(this.document.id, d.id);
        await this.load();
        this.$emit("changed");
      } catch (e) {
        this.showErrorSnackbar(this.$t("supplier_po.decision_error"));
      } finally {
        this.busy = "";
      }
    },
    channelIcon(c) {
      return { email: "mdi-email-outline", viber: "mdi-phone-message", whatsapp: "mdi-whatsapp", link: "mdi-link-variant" }[c] || "mdi-send";
    },
    statusColor(s) {
      return { confirm: "success", "confirm-half": "warning", "reject-supplier": "error" }[s];
    },
    availColor(a) {
      return { available: "success", partial: "warning", unavailable: "error" }[a];
    },
    diffAmount(i) {
      return Math.abs(i.offered_amount - i.requested_amount) > 0.000001;
    },
    diffPrice(i) {
      return i.requested_unit_price > 0 && i.unit_price !== null && Math.abs(i.unit_price - i.requested_unit_price) > 0.00005;
    },
    num(n) {
      return Number(n).toLocaleString("sr-Latn", { maximumFractionDigits: 3 });
    },
    money(n) {
      return n === null || n === undefined ? "" : Number(n).toLocaleString("sr-Latn", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    d8(v) {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString("sr-Latn");
    },
    dt(v, dateOnly) {
      if (!v) return "";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return v;
      return dateOnly
        ? d.toLocaleDateString("sr-Latn")
        : d.toLocaleDateString("sr-Latn") + " " + d.toLocaleTimeString("sr-Latn", { hour: "2-digit", minute: "2-digit" });
    },
  },
};
</script>

<style scoped>
.outlined-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
}
.changed-row {
  background: #fff8e1;
}
.decision {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  padding-top: 16px;
}
</style>
